import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  Language,
  License,
  useGetProviderQuery,
} from '../../../generated/graphql';
import Schedule from './scheduling/schedule';
import { formatProviderNameWithCredential } from '../../../helpers/format-helpers';
import { Icon } from '../../icon/icon';
import { theme } from '../../../styles/theme';
import { trackAdditionalMatchSelectedOrBooked } from '../../../helpers/analytics';
import { selectLdFlags } from '../../../redux/ld-flags';

type ScheduleModalProps = {
  id?: string;
  firstName?: string;
  lastName?: string;
  primaryCredential?: string;
  licenses?: License[];
  languages?: Language[];
  selectProvider: (appointmentTime?: string) => Promise<{ status: string }>;
  wasProviderAdditionalMatch?: boolean;
};

const ScheduleModal = ({
  id,
  firstName,
  lastName,
  primaryCredential,
  licenses,
  languages,
  selectProvider,
  wasProviderAdditionalMatch,
}: ScheduleModalProps) => {
  const [active, setActive] = useState(false);
  const [confirmingAppointment, setConfirmingAppointment] = useState(false);
  const toggleActive = () => !confirmingAppointment && setActive(!active);

  const { data, loading } = useGetProviderQuery({
    variables: { id },
  });

  const providerDetail = {
    ...data?.getProvider,
    licenses,
    languages,
  };

  useEffect(() => {
    if (active && wasProviderAdditionalMatch) {
      trackAdditionalMatchSelectedOrBooked(providerDetail.id, 'selected');
    }
  }, [active]);

  const useFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { shouldUseIntakeV2Point1 } = useSelector(selectLdFlags);

  return (
    <>
      <Button
        // Freshpaint ID for related event:
        // "Patient: Scheduling - Book Appointment"
        id="scheduling-book-appointment"
        onClick={toggleActive}
        variant="contained"
        fullWidth
      >
        {shouldUseIntakeV2Point1
          ? 'Choose a date and time'
          : 'Book Appointment'}
      </Button>
      <Dialog
        open={active}
        onClose={toggleActive}
        maxWidth="xs"
        fullScreen={useFullScreen}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {`Schedule your virtual appointment with ${formatProviderNameWithCredential(
            firstName,
            lastName,
            primaryCredential
          )}`}
          <IconButton
            onClick={toggleActive}
            sx={{ mr: -1 }}
            // Freshpaint ID for related event:
            // "Patient: Scheduling - Cancel Book Appointment"
            id="scheduling-cancel"
          >
            <Icon icon="Xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!loading && (
            <Schedule
              selectProvider={async appointmentTime =>
                selectProvider(appointmentTime)
              }
              provider={providerDetail}
              confirmingAppointment={confirmingAppointment}
              setConfirmingAppointment={setConfirmingAppointment}
              wasProviderAdditionalMatch={wasProviderAdditionalMatch}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScheduleModal;
